import { Injectable } from '@angular/core';
import moment from 'moment';
import { combineLatest, map, Observable } from 'rxjs';

import { LoadingStatus } from '../../../../../../core/domain/events/state_provider';
import { Dietitian } from '../../../../../../data/dietitian/domain/dietitian';
import { DietitianStateProvider } from '../../../../../../data/dietitian/domain/dietitian_state_provider';
import { PatientGroup } from '../../../../../../data/dietitian/domain/patient_group/patient_group';
import { ScheduledTaskCommands } from '../../../../../../data/dietitian/domain/scheduled_task/scheduled_task_commands';
import { InvalidScheduledTaskDateException } from '../../../../../../data/dietitian/domain/scheduled_task/scheduled_task_exceptions';
import { Survey } from '../../../../../../data/dietitian/domain/survey/survey';
import { SurveyCommands } from '../../../../../../data/dietitian/domain/survey/survey-commands';
import { Template } from '../../../../../../data/dietitian/domain/templates/template';
import {
  Patient,
  PatientId,
} from '../../../../../../data/patient/domain/patient';
import { PatientStateProvider } from '../../../../../../data/patient/domain/patient_state_provider';
import { PatientInfoViewModel } from '../send-question-modal/send-question-modal.facade';

@Injectable()
export class SendSurveyModalFacade {
  constructor(
    private patientStateProvider: PatientStateProvider,
    private dietitianStateProvider: DietitianStateProvider,
    private surveyCommands: SurveyCommands,
    private scheduledTaskCommands: ScheduledTaskCommands,
  ) {
    // Nada
  }

  get viewModel$(): Observable<PatientInfoViewModel> {
    return combineLatest({
      events: combineLatest({
        dietitianState: this.dietitianStateProvider.state$,
        patientState: this.patientStateProvider.state$,
      }),
    }).pipe(
      map((e) => {
        // update view model
        let error: string | undefined;

        if (e.events.dietitianState.loading == LoadingStatus.ERROR) {
          error = e.events.dietitianState.message;
        }
        if (e.events.patientState.loading == LoadingStatus.ERROR) {
          error = e.events.patientState.message;
        }

        return <PatientInfoViewModel>{
          isLoading:
            e.events.dietitianState.loading == LoadingStatus.LOADING ||
            e.events.patientState.loading == LoadingStatus.LOADING,
          error,
          dietitian: e.events.dietitianState.entity,
          patient: e.events.patientState.entity,
        };
      }),
    );
  }

  sendSurveyToGroupedPatients(
    template: Template,
    message: string | undefined,
    patientGroup?: PatientGroup,
  ): Promise<void> {
    return this.surveyCommands.sendSurveyToGroupedPatients(
      template,
      message,
      patientGroup,
    );
  }

  sendSurveyToSpecificPatient(
    template: Template,
    message: string | undefined,
    patientId: PatientId,
  ): Promise<Survey> {
    return this.surveyCommands.sendSurveyToSpecificPatient(
      template,
      message,
      patientId,
    );
  }

  sendSurveyToPatientSelection(
    template: Template,
    message: string | undefined,
    patients: Patient[],
  ): Promise<void> {
    return this.surveyCommands.sendSurveyToPatientSelection(
      template,
      message,
      patients,
    );
  }

  async getDietitianActiveSurvey(dietitian: Dietitian): Promise<Template[]> {
    return this.surveyCommands.getDietitianActiveSurvey(
      dietitian.dietitianId.id.toString(),
    );
  }

  async scheduleSurveyToPatientSelection(
    dietitian: Dietitian,
    currentTemplate: Template,
    messageTemplateContent: string,
    selectedPatients: Patient[],
    scheduledDate: Date,
    scheduledTime: string,
  ) {
    const scheduledAt = this.getScheduledDateTime(scheduledDate, scheduledTime);
    if (scheduledAt) {
      await this.scheduledTaskCommands.scheduleSurvey(
        dietitian,
        selectedPatients,
        currentTemplate,
        messageTemplateContent,
        scheduledAt,
      );
    } else {
      throw new InvalidScheduledTaskDateException();
    }
  }

  async scheduleSurveyToGroupedPatients(
    dietitian: Dietitian,
    currentTemplate: Template,
    messageTemplateContent: string,
    group: PatientGroup,
    scheduledDate: Date,
    scheduledTime: string,
  ) {
    const scheduledAt = this.getScheduledDateTime(scheduledDate, scheduledTime);
    if (scheduledAt) {
      await this.scheduledTaskCommands.scheduleSurvey(
        dietitian,
        group,
        currentTemplate,
        messageTemplateContent,
        scheduledAt,
      );
    } else {
      throw new InvalidScheduledTaskDateException();
    }
  }

  async scheduleSurveyToSpecificPatient(
    dietitian: Dietitian,
    currentTemplate: Template,
    messageTemplateContent: string,
    patient: Patient,
    scheduledDate: Date,
    scheduledTime: string,
  ) {
    const scheduledAt = this.getScheduledDateTime(scheduledDate, scheduledTime);
    if (scheduledAt) {
      await this.scheduledTaskCommands.scheduleSurvey(
        dietitian,
        patient,
        currentTemplate,
        messageTemplateContent,
        scheduledAt,
      );
    } else {
      throw new InvalidScheduledTaskDateException();
    }
  }

  private getScheduledDateTime(
    scheduledDate: Date,
    scheduledTime: string,
  ): Date | undefined {
    let scheduledAt: moment.Moment | undefined;
    if (scheduledDate && scheduledTime) {
      // merging date and time
      scheduledAt = moment(scheduledDate).startOf('day');
      const time = moment(scheduledTime, 'HH:mm');
      scheduledAt.set({
        hours: time.hours(),
        minutes: time.minutes(),
      });
    }
    if (!scheduledAt || scheduledAt.toDate() < new Date()) {
      throw new InvalidScheduledTaskDateException();
    }
    return scheduledAt?.toDate();
  }
}
